<template>
  <div v-if="chain != undefined" class="print:bg-white">
    <!-- PDF Generation Loading Overlay -->
    <div v-if="isGeneratingPdf" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white p-20 rounded-8 flex flex-col items-center space-y-12">
        <SparkSpinner class="w-32 h-32" />
        <div class="text-16 font-medium text-gray-900">Generating PDF...</div>
        <div class="text-13 text-gray-600">This may take a few moments</div>
      </div>
    </div>

    <div v-if="!analysisFinished" class="pl-16 p-4 text-13 text-red-500 bg-red-50 rounded-8">
      Warning: Part is not analyzed!
    </div>
    <div id="pdf-content" class="report flex flex-col space-y-8 p-20 bg-white min-h-screen">
      <div class="flex p-20 bg-white border border-gray-50 rounded-8 gap-20">
        <!-- Left column: Part information -->
        <div class="flex-1 flex flex-col space-y-12">
          <!-- Part Name -->
          <div class="font-bold text-16">{{ part.name }}</div>

          <!-- Main Process Chain -->
          <div class="flex text-13">
            <div class="w-1/3 text-gray-600">Main Process Chain</div>
            <div class="w-2/3 flex items-center gap-8 flex-wrap">
              <div>{{ chain.name }}</div>
              -
              <div>{{ chain.prc }}</div>
              <div class="flex w-[3px] h-[3px] bg-gray-400 rounded-full" />
              <div>{{ chain.material_name }}</div>
              <div class="flex w-[3px] h-[3px] bg-gray-400 rounded-full" />
              <div>{{ chain.machine_name }}</div>
            </div>
          </div>

          <!-- Benchmark Process Chain -->
          <div class="flex text-13 items-center">
            <div class="w-1/3 text-gray-600">Benchmark Process Chain</div>
            <div v-if="!benchmarkProcessChainSelect" class="flex items-center w-2/3">
              <div v-if="part.benchmark_process_chain_id != undefined">
                <div class="w-2/3 flex items-center gap-8 flex-wrap">
                  {{ part.process_chains[part.benchmark_process_chain_id]?.name }}
                  -
                  <div>{{ part.process_chains[part.benchmark_process_chain_id]?.prc }}</div>
                  <div class="flex w-[3px] h-[3px] bg-gray-400 rounded-full" />
                  <div>{{ part.process_chains[part.benchmark_process_chain_id]?.material_name }}</div>
                  <div class="flex w-[3px] h-[3px] bg-gray-400 rounded-full" />
                  <div>{{ part.process_chains[part.benchmark_process_chain_id]?.machine_name }}</div>
                </div>
              </div>
              <div v-else>No Benchmark selected</div>
              <i
                v-if="part.benchmark_process_chain_id != undefined"
                class="fas fa-edit ml-16 text-gray-800 cursor-pointer hover:text-primary-600"
                data-pdf-hide
                @click="benchmarkProcessChainSelect = true"
              />
            </div>
            <div v-else class="flex items-center w-2/3">
              <SparkSelect
                v-model="selectedBenchmarkProcessChainUid"
                custom="text-13 w-full"
                :options="benchmarkProcessChainOptions"
                name="benachmarkProcessChain"
                label=""
                @update:model-value="updateBenchmarkProcessChain"
              />
              <i
                class="fas fa-times ml-16 text-gray-800 cursor-pointer hover:text-primary-600 no-print"
                @click="benchmarkProcessChainSelect = false"
              />
            </div>
          </div>
        </div>

        <!-- Right column: Part thumbnail -->
        <div class="flex-shrink-0 flex items-center">
          <img
            v-if="partThumbnail"
            :src="partThumbnail"
            alt="Part Thumbnail"
            class="w-200 h-200 object-cover rounded-6 border border-gray-200"
            @error="handleImageError"
          />
          <div v-else class="w-200 h-200 bg-gray-100 rounded-6 border border-gray-200 flex items-center justify-center">
            <span class="text-gray-400 text-12">No Image</span>
          </div>
        </div>
      </div>
      <div class="flex gap-20">
        <div class="flex flex-col w-2/3 space-y-20 min-w-[650px] overflow-x-scroll">
          <div class="flex flex-col p-8 bg-white border border-gray-50 rounded-8">
            <div class="text-gray-900 text-16 flex p-20">
              <div>Summary</div>
              <div><FeasibilityValueAndPieChart class="h-24 ml-16" :percentage="overallFeasibility" /></div>
            </div>

            <div class="text-13 flex pl-20 min-w-[500px] overflow-x-scroll">
              <div v-if="chain.process_chain_id == part.primary_process_chain_id" class="w-1/2 space-y-12">
                <div class="flex">
                  <div class="text-gray-600 w-1/2">Cost Saved</div>
                  <div v-if="singleProcessChain" class="w-1/2">No Benchmark selected</div>
                  <div v-if="!singleProcessChain" class="flex w-1/2">
                    <div class="w-1/2 text-right">
                      {{ costSaved }}
                    </div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">€</div>
                  </div>
                </div>
                <div class="flex">
                  <div class="text-gray-600 w-1/2">Lead Time Saved</div>
                  <div v-if="singleProcessChain" class="w-1/2">No Benchmark selected</div>
                  <div v-if="!singleProcessChain" class="flex w-1/2">
                    <div class="w-1/2 text-right">
                      {{ leadtimeSaved }}
                    </div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">days</div>
                  </div>
                </div>
                <div class="flex">
                  <div class="text-gray-600 w-1/2">Carbon Emission Saved</div>
                  <div v-if="singleProcessChain" class="w-1/2">No Benchmark selected</div>
                  <div v-if="!singleProcessChain" class="flex w-1/2">
                    <div class="w-1/2 text-right">
                      {{ co2Saved }}
                    </div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">kg CO₂-eq</div>
                  </div>
                </div>
              </div>

              <div class="flex w-1/2 flex-col space-y-12">
                <div class="flex">
                  <div class="text-gray-600 w-1/2">Manufacturing Cost per Part</div>
                  <div class="flex w-1/2">
                    <div class="w-1/2 text-right">{{ $formatTwoDecimalPlaces(amCostPerPart) }}</div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">€</div>
                  </div>
                </div>
                <div class="flex">
                  <div class="text-gray-600 w-1/2">Quote Price per Part</div>
                  <div class="flex w-1/2">
                    <div class="w-1/2 text-right">{{ $formatTwoDecimalPlaces(marketPricePerPart) }}</div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">€</div>
                  </div>
                </div>
                <div class="flex">
                  <div class="text-gray-600 w-1/2">Carbon Emission per Lot</div>
                  <div class="flex w-1/2">
                    <div class="w-1/2 text-right">
                      {{ $formatTwoDecimalPlaces(chain.co2.co2_pl) }}
                    </div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">kg CO₂-eq</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="actualChainIsTechAM" class="flex flex-col p-8 bg-white border border-gray-50 rounded-8 text-13">
            <div class="text-gray-900 text-16 flex p-20">
              <div>Cost</div>
              <div><FeasibilityValueAndPieChart class="h-24 ml-16" :percentage="economicalFeasibility" /></div>
            </div>
            <div class="flex pb-20">
              <div class="flex w-1/2 flex-col space-y-12 pl-20">
                <div class="flex">
                  <div class="text-gray-600 w-1/2">Lot Size</div>
                  <div class="flex w-1/2">
                    <div class="w-1/2 text-right">{{ chain.lot_size }}</div>
                  </div>
                </div>
                <div class="flex">
                  <div class="text-gray-600 w-1/2">Cost per Lot</div>
                  <div class="flex w-1/2">
                    <div class="w-1/2 text-right">{{ $formatTwoDecimalPlaces(chain.cost.cost_pl) }}</div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">€</div>
                  </div>
                </div>
                <div class="flex">
                  <div class="text-gray-600 w-1/2">Parts per Job</div>
                  <div class="flex w-1/2">
                    <div class="w-1/2 text-right">{{ chain.parts_pj }}</div>
                  </div>
                </div>
                <div class="flex">
                  <div class="text-gray-600 w-1/2">Cost per Job</div>
                  <div class="flex w-1/2">
                    <div class="w-1/2 text-right">{{ $formatTwoDecimalPlaces(chain.cost.cost_pj) }}</div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">€</div>
                  </div>
                </div>
              </div>
              <div class="flex w-1/2 flex-col space-y-12">
                <div class="flex">
                  <div class="text-gray-600 w-1/2 flex items-center">
                    <div :style="{ 'background-color': paletteArray[0] }" class="w-16 h-16 rounded-6 mr-8" />
                    <div>Pre-Process</div>
                  </div>
                  <div class="flex w-1/2">
                    <div class="w-1/2 text-right">{{ $formatTwoDecimalPlaces(amCostBreakdown.pre_process) }}</div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">€</div>
                  </div>
                </div>
                <div class="flex">
                  <div class="text-gray-600 w-1/2 flex items-center">
                    <div :style="{ 'background-color': paletteArray[1] }" class="w-16 h-16 rounded-6 mr-8" />
                    <div>In-Process</div>
                  </div>
                  <div class="flex w-1/2">
                    <div class="w-1/2 text-right">{{ $formatTwoDecimalPlaces(amCostBreakdown.in_process) }}</div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">€</div>
                  </div>
                </div>
                <div class="flex">
                  <div class="text-gray-600 w-1/2 flex items-center">
                    <div :style="{ 'background-color': paletteArray[2] }" class="w-16 h-16 rounded-6 mr-8" />
                    <div>Post-Process</div>
                  </div>
                  <div class="flex w-1/2">
                    <div class="w-1/2 text-right">{{ $formatTwoDecimalPlaces(amCostBreakdown.post_process) }}</div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">€</div>
                  </div>
                </div>
                <div class="flex">
                  <div class="text-gray-600 w-1/2 flex items-center">
                    <div :style="{ 'background-color': paletteArray[3] }" class="w-16 h-16 rounded-6 mr-8" />
                    <div>Material</div>
                  </div>
                  <div class="flex w-1/2">
                    <div class="w-1/2 text-right">{{ $formatTwoDecimalPlaces(amCostBreakdown.material) }}</div>
                    <div class="text-gray-400 pl-4 w-1/2 flex-start">€</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="feastable pagebreak-before bg-white border border-gray-50 rounded-8 pl-20 pb-20">
            <FeasibilityChecksList :active-process-chain="chain" />
          </div>
        </div>

        <div class="flex flex-col w-1/3">
          <div v-if="actualChainIsTechAM" class="flex bg-white border border-gray-50 rounded-8 mb-20">
            <div class="flex flex-col w-full">
              <div class="text-gray-900 text-16 p-20">
                <div>AM Manufacturing Cost per Part</div>
              </div>

              <CostChart :chain="chain" :label="label" short-bool @mounted="childComponentsMounted += 1" />
            </div>
          </div>

          <div class="flex flex-col p-20 bg-white border border-gray-50 rounded-8 text-13 w-full min-w-[300px] mb-20">
            <div class="text-gray-900 text-16 flex pb-12">
              <div>Technology Overview</div>
            </div>

            <table class="w-full">
              <tbody>
                <tr>
                  <td class="text-gray-600 w-1/2 py-8">Part Mass</td>
                  <td class="w-1/2">
                    <div class="flex justify-end">
                      <span>{{ $formatZeroDecimalPlaces(chain.part_mass) }}</span>
                      <span class="text-gray-400 pl-4 w-12">g</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="text-gray-600 w-1/2 py-8">Part Volume</td>
                  <td class="w-1/2">
                    <div class="flex justify-end">
                      <span>{{ $formatOneDecimalPlace(part.part_vol) }}</span>
                      <span class="text-gray-400 pl-4 w-12">cm³</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="text-gray-600 w-1/2 py-8">Bounding Box</td>
                  <td class="w-1/2">
                    <div class="flex justify-end">
                      <span
                        >{{ $formatOneDecimalPlace(part.bb_x) }} x {{ $formatOneDecimalPlace(part.bb_y) }} x
                        {{ $formatOneDecimalPlace(part.bb_z) }}</span
                      >
                      <span class="text-gray-400 pl-4 w-12">mm</span>
                    </div>
                  </td>
                </tr>

                <template v-if="actualChainIsTechAM">
                  <tr>
                    <td class="text-gray-600 w-1/2 py-8">Part Support Mass</td>
                    <td class="w-1/2">
                      <div class="flex justify-end">
                        <span
                          >{{ $formatZeroDecimalPlaces(chain.sup_mass) }} ({{
                            $formatZeroDecimalPlaces(supportPercent)
                          }}%)</span
                        >
                        <span class="text-gray-400 pl-4 w-12">g</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="text-gray-600 w-1/2 py-8">Minimum Bounding Box</td>
                    <td class="w-1/2">
                      <div class="flex justify-end">
                        <span
                          >{{ $formatOneDecimalPlace(part.min_bb_x) }} x {{ $formatOneDecimalPlace(part.min_bb_y) }} x
                          {{ $formatOneDecimalPlace(part.min_bb_z) }}</span
                        >
                        <span class="text-gray-400 pl-4 w-12">mm</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="text-gray-600 w-1/2 py-8">Number of Layers</td>
                    <td class="w-1/2">
                      <div class="flex justify-end">
                        <span>{{ chain.n_lyr }}</span>
                        <span class="text-gray-400 pl-4 w-12"></span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="text-gray-600 w-1/2 py-8">Full Job Build Height</td>
                    <td class="w-1/2">
                      <div class="flex justify-end">
                        <span>{{ $formatOneDecimalPlace(chain.bld_h) }}</span>
                        <span class="text-gray-400 pl-4 w-12">mm</span>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div
            v-if="shouldShowCarbonChart"
            class="flex flex-col p-20 bg-white border border-gray-50 rounded-8 text-13 w-full min-w-[300px]"
          >
            <CarbonChartComparisonChain
              :process-chains="carbonProcessChains"
              :loading="!analysisFinished"
              total-unit="kg CO2-eq"
            />
          </div>
        </div>
      </div>

      <div class="mb-64">
        <div
          class="processchain pagebreak-before pagebreak-after bg-white border border-gray-50 rounded-8 pl-20 pb-20 mb-64"
        >
          <div class="text-gray-900 text-16 flex p-20">Process Chain</div>
          <ProcessChain :chain="chain" :editable="false" :display-title="false" :pdf-view="true" class="text-13" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import CostChart from '@/components/PartWorkflow/Cost/CostChart.vue';
import FeasibilityChecksList from '@/components/PartWorkflow/Feasibility/FeasibilityChecksList.vue';
import FeasibilityValueAndPieChart from '@/components/PartWorkflow/Feasibility/FeasibilityValueAndPieChart.vue';
import ProcessChain from '@/components/PartWorkflow/ProcessChain/ProcessChain.vue';
import SparkSelect from '@/components/SparkComponents/SparkSelect.vue';
import CarbonChartComparisonChain from '@/components/PartWorkflow/Overview/CarbonChartComparisonChain.vue';
import SparkSpinner from '@/components/SparkComponents/SparkSpinner.vue';
import { analysisFinished } from '@/composables/processChainHandling.js';
import { usePdfGenerator } from '@/composables/pdfGenerator.ts';
import {
  combineCarbonSegments,
  createPartSegments,
  calculateMaterialEnergyConsumption,
} from '@/utils/carbonCalculation';

export default {
  name: 'DownloadPdf',

  components: {
    CostChart,
    FeasibilityChecksList,
    ProcessChain,
    FeasibilityValueAndPieChart,
    SparkSelect,
    CarbonChartComparisonChain,
    SparkSpinner,
  },

  setup() {
    const { generatePDF } = usePdfGenerator();

    return {
      generatePDF,
    };
  },

  data() {
    return {
      benchmarkProcessChainSelect: false,
      childComponentsMounted: 0,
      selectedBenchmarkProcessChainUid: '',
      isGeneratingPdf: false,
      partThumbnail: null,
      label: {
        labor: 'Labor',
        machine: 'Machine',
        other: 'Other',
        build_material: 'Build Material',
        support_material: 'Support Material',
        waste_material: 'Waste Material',
      },
      selectedCarbonLevel: { _pl: 'Lot' }, // Default to lot level for carbon calculations
    };
  },

  computed: {
    ...mapState(['part', 'user']),
    ...mapState('application', ['axiosInstance']),
    benchmarkProcessChainUid() {
      return this.part.benchmark_process_chain_id;
    },

    costSaved() {
      if (this.part.cost_saved * this.chain.lot_size < 0) return '-';
      else {
        return this.$formatTwoDecimalPlaces(this.part.cost_saved * this.chain.lot_size);
      }
    },

    leadtimeSaved() {
      if (this.part.leadtime_saved < 0) return '-';
      else {
        return this.part.leadtime_saved;
      }
    },

    co2Saved() {
      let primaryco2 = this.chain?.co2?.co2_pl;
      if (this.benchmarkProcessChainUid) {
        let benchmarkco2 = this.processChains[this.benchmarkProcessChainUid]?.co2?.co2_pl;

        if (benchmarkco2 - primaryco2 < 0) return '-';
        else return this.$formatTwoDecimalPlaces(benchmarkco2 - primaryco2);
      } else {
        return '-';
      }
    },

    singleProcessChain() {
      return Object.keys(this.processChains).length == 1;
    },

    pdfRequested() {
      return this.part.pdf_requested;
    },

    chain() {
      let process_chain_id = this.$route.params.process_chain_id;
      if (this.processChains != undefined) {
        return this.processChains[process_chain_id];
      } else {
        return {};
      }
    },

    benchmarkProcessChainOptions() {
      let options = [];
      Object.keys(this.processChains).forEach(key => {
        if (key != this.chain.process_chain_id) {
          options.push({ value: key, label: this.processChains[key].name });
        }
      });
      return options;
    },

    processChains() {
      return this.part.process_chains;
    },

    actualChainIsTechAM() {
      return this.chain?.tech == 'additive_manufacturing';
    },

    paletteArray() {
      return getComputedStyle(document.body).getPropertyValue('--chart-palette').split(',');
    },

    amCostBreakdown() {
      const obj = this.chain.cost_breakdown || {};
      const categories = ['pre_process', 'in_process', 'post_process', 'material'];

      return categories.reduce((result, category) => {
        const categoryData = obj[category] || {};
        result[category] = Object.values(categoryData).reduce((sum, value) => {
          return sum + (Number.isFinite(value) ? value : 0);
        }, 0);
        result[category] = Number(result[category].toFixed(2));
        return result;
      }, {});
    },

    amCostPerPart() {
      return this.chain?.cost.cost_pp;
    },

    supportPercent() {
      return (this.chain.sup_mass / this.chain.part_mass) * 100;
    },

    marketPricePerPart() {
      let marketPrice = this.chain.market_price.market_price_pp;
      if (marketPrice.user == null) {
        return marketPrice.calculated;
      } else {
        return marketPrice.user;
      }
    },

    economicalFeasibility() {
      return this.chain?.float_suitabilities?.economic;
    },

    overallFeasibility() {
      return this.chain?.float_suitabilities?.overall;
    },

    analysisFinished() {
      return analysisFinished(this.chain.process_chain_id);
    },

    // Generate real carbon footprint data from process chains
    carbonProcessChains() {
      if (!this.processChains || !this.chain) return [];

      const level = Object.keys(this.selectedCarbonLevel)[0];
      const chains = [];

      // Add current chain
      const carbonData = this.generateCarbonSegments(this.chain, level);
      if (carbonData.segments.length > 0) {
        chains.push({
          name: this.chain.name,
          segments: carbonData.segments,
          total: carbonData.totalCo2,
        });
      }

      // Add benchmark chain if available and different from current
      if (
        this.benchmarkProcessChainUid &&
        this.processChains[this.benchmarkProcessChainUid] &&
        this.benchmarkProcessChainUid !== this.chain.process_chain_id
      ) {
        const benchmarkChain = this.processChains[this.benchmarkProcessChainUid];
        const benchmarkCarbonData = this.generateCarbonSegments(benchmarkChain, level);
        if (benchmarkCarbonData.segments.length > 0) {
          chains.push({
            name: `${benchmarkChain.name} (Benchmark)`,
            segments: benchmarkCarbonData.segments,
            total: benchmarkCarbonData.totalCo2,
          });
        }
      }

      return chains;
    },

    shouldShowCarbonChart() {
      return this.carbonProcessChains.length > 0 && this.analysisFinished;
    },
  },

  watch: {
    pdfRequested(newVal) {
      if (newVal == true) {
        setTimeout(this.print, 1000);
        this.requestPdf(false);
      }
    },

    'part.part_id': {
      immediate: true,
      handler() {
        this.fetchPartThumbnail();
      },
    },
  },

  mounted() {
    this.selectedBenchmarkProcessChainUid = this.benchmarkProcessChainUid;
  },

  methods: {
    ...mapMutations('canvas', ['updateReloadCanvas']),
    ...mapMutations(['updatePart', 'changeProcessChainUids']),
    ...mapMutations({
      requestPdf: 'pdfRequested',
    }),

    generateCarbonSegments(processChain, level) {
      if (!processChain || !processChain.co2) {
        return { segments: [], totalCo2: 0 };
      }

      const materialEnergyConsumption = calculateMaterialEnergyConsumption(processChain, level);
      const partSegments = createPartSegments(processChain, level);
      const segments = combineCarbonSegments(materialEnergyConsumption, partSegments);
      const totalCo2 = processChain.co2['co2' + level] || 0;

      return {
        segments,
        totalCo2: Number(totalCo2.toFixed(2)),
        materialEnergyConsumption,
        partEnergyConsumption: totalCo2 - materialEnergyConsumption,
      };
    },

    updateBenchmarkProcessChain() {
      let formData = {};
      formData['benchmark_process_chain_id'] = this.selectedBenchmarkProcessChainUid;
      this.axiosInstance.put(`/api/v1/part/${this.part.part_id}/`, formData).then(response => {
        this.changeProcessChainUids(formData);
        this.updatePart(response.data.part);
      });
    },

    async print() {
      try {
        this.isGeneratingPdf = true;

        // Generate PDF using the new method
        console.log('Attempting PDF generation with pdfGenerator...');
        const result = await this.generatePDF('pdf-content', `${this.part.name}_analysis_report.pdf`, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
        });

        if (result.success) {
          console.log('PDF generated successfully using pdfGenerator');
        } else {
          console.error('PDF generation failed, falling back to browser print:', result.error);
          // Fallback to browser print if PDF generation fails
          this.fallbackPrint();
        }
      } catch (error) {
        console.error('PDF generation error, falling back to browser print:', error);
        // Fallback to browser print
        this.fallbackPrint();
      } finally {
        this.isGeneratingPdf = false;
      }
    },

    fallbackPrint() {
      console.log('Using fallback browser print method');
      this.setPrintDisplay('none'); // Hide elements

      // Also hide data-pdf-hide elements for fallback print
      const elementsToHide = document.querySelectorAll('[data-pdf-hide]');
      const hiddenElements = [];
      elementsToHide.forEach(el => {
        const htmlElement = el;
        if (htmlElement.style.display !== 'none') {
          hiddenElements.push({
            element: htmlElement,
            originalDisplay: htmlElement.style.display || '',
          });
          htmlElement.style.display = 'none';
        }
      });

      window.print();

      // Restore data-pdf-hide elements
      hiddenElements.forEach(({ element, originalDisplay }) => {
        element.style.display = originalDisplay;
      });

      this.setPrintDisplay(''); // Show elements
    },

    setPrintDisplay(value) {
      // Popup
      const popupElements = document.getElementsByClassName('confirm');
      for (let i = 0; i < popupElements.length; i++) {
        popupElements[i].style.display = value;
      }

      // SubNav
      const subNavElements = document.getElementsByClassName('navi');
      for (let i = 0; i < subNavElements.length; i++) {
        subNavElements[i].style.display = value;
      }

      // Footer
      const footerElements = document.getElementsByClassName('part-footer');
      for (let i = 0; i < footerElements.length; i++) {
        footerElements[i].style.display = value;
      }

      // Navbar
      const navElement = document.getElementById('nav');
      if (navElement) {
        navElement.style.display = value;
      }

      // Buttons
      const buttonsElement = document.getElementById('buttons');
      if (buttonsElement) {
        buttonsElement.style.display = value;
      }

      const chatIcon = document.getElementById('chatling-open-chat-icon');
      if (chatIcon) {
        chatIcon.style.display = value;
      }
    },

    async fetchPartThumbnail() {
      if (!this.part.part_id) {
        this.partThumbnail = null;
        return;
      }

      try {
        const response = await this.axiosInstance.get(`api/v1/part-thumbnail/${this.part.part_id}/`, {
          responseType: 'blob',
        });
        const fileReader = new FileReader();
        fileReader.readAsDataURL(response.data);
        fileReader.onload = e => {
          this.partThumbnail = e.target.result;
        };
      } catch (error) {
        console.log('Error fetching part thumbnail:', error);
        this.partThumbnail = null;
      }
    },

    handleImageError() {
      this.partThumbnail = null;
    },
  },
};
</script>

<style lang="scss" scoped>
// PDF-specific styles
#pdf-content {
  font-family: 'Poppins', sans-serif;
  max-width: 100%;
  box-sizing: border-box;

  // Add internal padding for better spacing
  padding: 20px !important;
  margin: 0 auto;

  // Ensure charts and images render properly in PDF
  .chart-container,
  canvas,
  svg {
    max-width: 100%;
    height: auto;
  }

  // Better spacing for PDF sections
  .report-section {
    margin-bottom: 24px;
    page-break-inside: avoid;
  }

  // Add spacing between major sections
  > div {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  // Ensure text is readable in PDF
  .text-gray-400,
  .text-gray-600 {
    color: #6b7280 !important;
  }

  .text-gray-900 {
    color: #111827 !important;
  }

  // Part thumbnail styling for PDF
  img {
    max-width: 100% !important;
    height: auto !important;
    display: block !important;
  }

  .w-60 {
    width: 60px !important;
  }

  .h-60 {
    height: 60px !important;
  }

  .w-200 {
    width: 200px !important;
  }

  .h-200 {
    height: 200px !important;
  }

  // Make sure backgrounds are visible in PDF
  .bg-white {
    background-color: #ffffff !important;
  }

  .bg-red-50 {
    background-color: #fef2f2 !important;
  }

  .text-red-500 {
    color: #ef4444 !important;
  }

  // Ensure borders are visible
  .border {
    border-width: 1px !important;
  }

  .border-gray-50 {
    border-color: #f9fafb !important;
  }

  // Ensure rounded corners work in PDF
  .rounded-8 {
    border-radius: 8px !important;
  }

  // Fix process step names being cut off in PDF
  .process-step-name {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: unset !important;
    max-width: none !important;
    word-wrap: break-word !important;
    line-height: 1.4 !important;
    min-height: auto !important;
    height: auto !important;
  }

  // Fix truncated text in process steps
  .truncate,
  .process-step-container {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: unset !important;
    height: auto !important;
  }

  // Specific styling for process chain section in PDF
  .processchain {
    .flex.justify-between {
      gap: 16px !important;
    }

    .w-1\/3 {
      min-width: 200px !important;
    }
  }
}

@media print {
  @page {
    margin: 1.6cm;
    background-color: white;
  }

  .pagebreak-before {
    page-break-before: always;
  }

  .pagebreak-after {
    page-break-after: always;
  }
}
</style>
